interface GlobalWithProcessEnv {
  process?: {
    env?: Record<string, string | undefined>;
  };
}

export const getGlobalEnvVar = (key: string): string | undefined => {
  const globalWithEnv = globalThis as unknown as GlobalWithProcessEnv;
  return globalWithEnv.process?.env?.[key];
};

/**
 * Safely parses a date string to a Unix timestamp.
 */
export const safeParseDate = (dateString?: string | null): number | null => {
  if (!dateString) return null;
  const parsed = Date.parse(dateString);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Derives the Amplitude user-id environment suffix from the portal proxy origin
 * (AMPLITUDE_PROXY_ORIGIN). Cross-domain apps such as ia.rapidsos.com cannot rely
 * on window.location hostname.
 *
 * Portal hosts follow `{env}.rapidsosportal.com`; the env slug becomes the suffix.
 * Bare `rapidsosportal.com` maps to `prod`. Missing or unrecognised origins default to `dev`.
 */
export const getAmplitudeEnvironmentSuffix = (proxyOrigin?: string): string => {
  const origin = proxyOrigin?.trim();
  if (origin) {
    try {
      const hostname = new URL(origin).hostname;
      if (hostname === 'rapidsosportal.com') {
        return 'prod';
      }

      const portalSubdomainMatch = hostname.match(
        /^([a-z0-9-]+)\.rapidsosportal\.com$/,
      );
      const suffix = portalSubdomainMatch?.[1];
      if (suffix) {
        return suffix;
      }
    } catch {
      // Fall through to dev default below.
    }
  }

  return 'dev';
};

/**
 * Helper function to ensure undefined values are preserved in nested objects
 */
export const preserveUndefined = (obj: unknown): unknown => {
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        value === undefined ? null : preserveUndefined(value),
      ]),
    );
  }
  return obj;
};

/**
 * Default error capture function that logs to console
 */
export const defaultCaptureError = (
  error: Error,
  scope: Record<string, unknown> = {},
): void => {
  if (getGlobalEnvVar('NODE_ENV') === 'development') {
    // eslint-disable-next-line no-console
    console.error('[GainSight Error]', error.message, {
      error,
      scope,
      stack: error.stack,
    });
  }
};
